// Shared between the client portal and the staff workspace (owner 2026-10-15): helpers for the
// document create/edit dialogs, extracted mechanically from components/ClientPortal/index.tsx.
// Behavior must stay identical for the portal — all server access goes through MerchantDocApi.
import React, { useCallback, useEffect, useRef } from 'react';
import type { TFunction } from 'i18next';
import { todayISO } from '../../utils/region';

/** Server access for the document dialogs, injected by the host screen: the portal maps each
 *  member to the exact portal API call the code made before the extraction. */
export type MerchantDocApi = {
  customers: (page?: number, per?: number, q?: string) => Promise<any>;
  saveCustomer: (id: string | null, b: any) => Promise<any>;
  products: () => Promise<any>;
  createInvoice: (b: any) => Promise<any>;
  createQuotation: (b: any) => Promise<any>;
  updateDoc: (kind: 'invoice' | 'quotation', id: string, b: any) => Promise<any>;
  uploadDocImage: (f: File) => Promise<any>;
  docImageBlob: (url: string) => Promise<string>;
  docs: (kind: 'invoice' | 'receipt', page?: number, per?: number, q?: string) => Promise<any>;
  createNote: (b: any) => Promise<any>;
  /** The document a note adjusts, as the server reads it for the note window: its STORED lines in
   *  the editor's shape, its currency and whether a debit note against it would split a marked-up
   *  line (GET …/notes/source/:kind/:id, routes/portalDocs.ts noteSourceRoute). A credit note is
   *  filled from these lines (owner, second round, 2026-10-20 — option B). ⛔ REQUIRED: both hosts
   *  that open CreateNoteModal must serve it, so the staff window and the client's portal window
   *  are filled alike. */
  noteSource: (kind: 'invoice' | 'receipt', id: string) => Promise<any>;
  /** (Mark-up round 5) What createNote would answer, nothing issued (POST …/notes/preflight — the server's own route,
   *  rolled back before it commits): { ok: true } or { ok: false, status, refusal }. The note window's Issue asks it
   *  first and sends nothing bound to be refused. Optional: a host without it issues as before. */
  notePreflight?: (b: any) => Promise<any>;
  // ── The receipt family (FS-4, owner 2026-10-19) ───────────────────────────────────────────────
  // Lifted out of the portal with the dialogs in ReceiptModals.tsx. The portal maps each one
  // straight back to the call it made inline before the lift, so nothing about issuing a receipt
  // changed for a merchant.
  // ⛔ OPTIONAL, like saleAccounts and fxHint above, and for the same reason: a host that does not
  // open the receipt dialogs provides none of them, and must not be made to invent three members to
  // keep compiling. A host that DOES open them is typed MerchantReceiptApi below, which is where
  // the compiler asks for all three.
  /** The invoices a receipt can be issued against: GET /docs/invoice?unpaid=1, newest first. `id`: that one row, for a window preset to it (owner 2026-11-02). */
  unpaidInvoices?: (q?: string, id?: string) => Promise<any>;
  createReceipt?: (b: any) => Promise<any>;
  /** ⛔ The LIMITED patch, and the server means it: remark + bankAccountId are the only fields it
   *  writes on a receipt, and a Thai one additionally requires `reason`. See ReceiptModals.tsx. */
  updateReceipt?: (id: string, b: any) => Promise<any>;
  /** code -> account chip data for document lines ({ byCode }). Staff workspace only; the portal
   *  deliberately does not provide it — the merchant never sees the firm's bookkeeping chart. */
  saleAccounts?: () => Promise<any>;
  /** A saved document's supporting files and linked records, for its edit window (owner 2026-11-02). Staff workspace only: ⛔ the portal
   *  never sees them and provides nothing here, so its windows (testFsTail T2-5's DOM hashes) draw nothing. */
  evidence?: (kind: string, id: string) => React.ReactNode;
  /** The BOT rate published on the last business day of a month, for the currency on the document.
   *  Staff workspace only, like saleAccounts: it is shown BESIDE the rate somebody typed so a wrong
   *  one is visible, and it decides nothing — which rate a document uses is a tax position the firm
   *  takes, not a default this screen applies. */
  fxHint?: (currency: string, ym: string) => Promise<any>;
  /** Batch 11 R3 (design §3.5.4 row 3): what recording a receipt on this invoice would meet — 200 { lock }, the in-books
   *  lock on the hand-entered VAT line it would supersede. Both hosts that open CreateReceiptModal serve it; asked when
   *  the invoice is chosen, so Save is disabled with the sentence instead of sending a certain 409. */
  receiptPreflight?: (invoiceId: string, date?: string) => Promise<any>;
  /** (Review 2026-11-02) What a free New tax invoice or cash sale would meet at Issue on what its form holds (GET …/create-doc-gate, merchantCreateDoc's own gates): `{ refusal }`, null when it may go.
   *  Optional: a host without it sends the date and shows the server's answer, as before. */
  createDocGate?: (ask: { docType: string; date: string; currency: string; fxRate: number; total: number }) => Promise<any>;
  /** (Review 2026-11-02) What an edit moving this document to `date` would meet (GET …/editable?date=, docPatchRoute's own questions): `{ dateRefusal }`, null when it may go. Optional, like the one above. */
  editDate?: (kind: 'invoice' | 'quotation', id: string, date: string) => Promise<any>;
  /** (Review 2026-11-03) Whether this document may still be edited as the window opened it (GET …/editable, the answer that opened it), asked again at Save: a document can post itself
   *  while its window stands open (server services/autoPost.ts), and a refused save pages the admins. Optional: a host without it shows the server's answer, as before. */
  editableNow?: (kind: 'invoice' | 'quotation', id: string) => Promise<any>;
  /** (COA-D6, owner 2026-10-28) The tax invoice form's "For which paid invoice" picker: this customer's ordinary invoices
   *  paid in full, with no tax invoice issued for them yet (GET …/tax-for-candidates — the server's own rule), each with
   *  what was paid and its lines. Optional: a host without it offers no picker, and the form issues as before. */
  taxForCandidates?: (customerId: string) => Promise<any>;
  /** (b14, orchestrator 2026-10-28) The pre-check of a tax invoice for a paid invoice dated `date` (GET …/tax-for-date — the
   *  server's own taxForDateRefusal: before the sale, a closed period, a filed month): `{ refusal }`, null when it may
   *  stand. Optional: a host without it sends the date and shows the server's answer, as before. */
  taxForDate?: (forId: string, date: string) => Promise<any>;
  /** (Owner 2026-11-02) What a quotation may become, asked with what the seeded form holds (GET …/invoice-impact, the server's one verdict). Staff only, like everything seeded. */
  quotationImpact?: (id: string, ask: { date: string; currency: string; fxRate: number; total: number; forDocId?: string }) => Promise<any>;
  /** (Owner 2026-11-02) This customer's bills from a quotation still owing: the New window's warning. Staff only. */
  quotationBills?: (customerId: string) => Promise<any>;
  /** (Owner 2026-11-02) What a copied form would meet at Issue on what it holds now (GET …/copy-gate): `{ refusal }`, null when it may go. Staff only, like Copy. */
  copyGate?: (id: string, ask: { customerId: string; date: string; currency: string; fxRate: number; total: number }) => Promise<any>;
};

/** A host that opens the receipt dialogs (ReceiptModals.tsx) proves it can serve them. Narrowing
 *  here rather than making the three members required on MerchantDocApi is what lets a host that
 *  shows receipts read-only stay exactly as it is. */
export type MerchantReceiptApi = MerchantDocApi & Required<Pick<MerchantDocApi, 'unpaidInvoices' | 'createReceipt' | 'updateReceipt'>>;

/** The server's coded refusals for a DOCUMENT being issued or voided (routes/portalDocs.ts, receipts
 *  round 3, owner 2026-10-20 「已结账期间」), in the operator's language. The server sends a `code` and
 *  the figures beside its own English; this words them. '' for anything else — the caller then shows
 *  what the server said, as it always did.
 *    · period_closed        — a tax invoice / cash sale / credit or debit note dated in a closed period;
 *    · period_closed_void   — voiding (or deleting) a document dated in a closed period;
 *    · period_closed_edit   — editing a tax invoice / cash sale dated in a closed period (round-3 review);
 *    · import_period_closed — a spreadsheet with rows dated in a closed period (nothing imported). */
export const docRefusalText = (e: any, t: TFn): string => {
  const p = e?.payload && typeof e.payload === 'object' ? e.payload : {};
  if (p.code === 'period_closed') return t('p_doc_err_period_closed', { date: p.date, from: p.periodStart, to: p.periodEnd });
  if (p.code === 'period_closed_void') return t('p_doc_err_period_closed_void', { docNo: p.docNo, date: p.date, from: p.periodStart, to: p.periodEnd });
  if (p.code === 'period_closed_edit') return t('p_doc_err_period_closed_edit', { docNo: p.docNo, date: p.date, from: p.periodStart, to: p.periodEnd });
  if (p.code === 'import_period_closed') return t('p_imp_err_period_closed', { n: p.count, rows: p.rows, periods: p.periods });
  // void_receipts_first (round-3 review) — a document (a debit note too, now) that still holds live receipts.
  if (p.code === 'void_receipts_first') return t('p_doc_err_void_receipts_first', { docNo: p.docNo });
  return '';
};

// ── Receipts round 4 (owner 2026-10-20, rebuilt 2026-10-26) ───────────────────────────────────────
/** Which documents a receipt is a TAX INVOICE for — the rule its printed title follows (server
 *  services/thaiTaxDocs.ts receiptIsTaxInvoice, which routes/portalDocs.ts noteRefOf applies): a
 *  VAT-registered merchant's receipt for a document that did not book its own VAT (not a tax invoice, a
 *  cash sale or a debit note). Only such a receipt may be adjusted by a credit or debit note, so the
 *  note window offers only these (item 5) — the same rule, so it never offers one the server refuses. */
export const receiptIsTaxInvoiceOf = (vatRegistered: boolean, settlesDocType: any): boolean =>
  !!vatRegistered && !['tax_invoice', 'tax_invoice_receipt', 'credit_note', 'debit_note'].includes(String(settlesDocType || ''));
/** A document named by its own type in a refusal (item 8: "Debit note DN-…", "Tax invoice TIV-…") —
 *  the workspace's own type words (aw_dt_*), so a bill reads "Invoice …" exactly as before. `lc`: the
 *  same word mid-sentence (lower-cased in English; Chinese and Thai have no case). */
const DOC_TYPE_WORD: Record<string, string> = {
  invoice: 'aw_dt_invoice', proforma: 'aw_dt_proforma', tax_invoice: 'aw_dt_tax_invoice', tax_invoice_receipt: 'aw_dt_tax_invoice_receipt',
  credit_note: 'aw_dt_credit_note', debit_note: 'aw_dt_debit_note',
};
export const docTypeWord = (t: TFn, docType: any): { docWord: string; docWordLc: string } => {
  const w = String(t(DOC_TYPE_WORD[String(docType || '')] || 'aw_dt_invoice'));
  return { docWord: w, docWordLc: w.toLowerCase() };
};

export const ACCENT = '#0E7C66';
export const card: React.CSSProperties = { background: '#fff', border: '1px solid #e3e7ea', borderRadius: 12, padding: 20 };
export const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 14, border: '1px solid #d3dbe2', borderRadius: 8, boxSizing: 'border-box', outline: 'none' };
export const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#5b6b79', display: 'block', margin: '10px 0 4px' };
export const btn: React.CSSProperties = { padding: '9px 18px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' };
export const btnGhost: React.CSSProperties = { ...btn, background: '#eef2f5', color: '#333' };

// ── Line arithmetic — ONE copy, shared by the document modal and the credit/debit note modal ──
// This MUST mirror calcItems() in server/src/routes/portalDocs.ts exactly, including the rounding
// points: the server rounds every line and every running total to 2dp, so a preview that adds raw
// floats drifts a satang off the stored document on ~1 document in 4. Round the same way, here.
export const VAT_RATE = 7;
export const r2 = (n: number) => Math.round(n * 100) / 100;
// Localised, so it has to be built per render rather than held in a module constant. The rate is
// still interpolated from VAT_RATE — the label must not drift from the arithmetic below.
// i18next's own type. A hand-rolled `(key: string, opts?) => string` does NOT accept the hook's t:
// TFunction can return TFunctionDetailedResult, so assigning it loses at the call site (TS2345).
export type TFn = TFunction<'translation', undefined>;
export const vatOptions = (t: TFn): { value: string; label: string; hint: string }[] => [
  { value: 'standard',      label: t('p_vat_opt_standard', { rate: VAT_RATE }),      hint: t('p_vat_hint_standard') },
  { value: 'standard_incl', label: t('p_vat_opt_standard_incl', { rate: VAT_RATE }), hint: t('p_vat_hint_standard_incl') },
  { value: 'zero',          label: t('p_vat_opt_zero'),                              hint: t('p_vat_hint_zero') },
  { value: 'exempt',        label: t('p_vat_opt_exempt'),                            hint: t('p_vat_hint_exempt') },
  { value: 'none',          label: t('p_vat_opt_none'),                              hint: t('p_vat_hint_none') },
];
// ── Whose charge is this line: ours, or money paid out for the customer? ─────────────────────────
// ONE control, TWO stored fields (server charge_role + supplier_invoice_billed_to, db/index.ts).
// They are two fields because the question has two halves — is this a pass-through, and whose name
// is on the supplier's bill — and neither half decides anything alone. They are one control because
// nobody invoicing a port handling fee should have to answer two dropdowns to describe one fact.
//
// The user is asked WHOSE NAME IS ON THE SUPPLIER'S BILL. That is a question about a piece of paper
// they are holding. They are never asked whether something is "out of scope", which is a tax
// position and not theirs to take.
export const CHARGE_CHOICES: { value: string; role: string; billed: string }[] = [
  { value: 'own',  role: 'own_supply',   billed: '' },
  { value: 'cust', role: 'disbursement', billed: 'customer' },
  { value: 'self', role: 'disbursement', billed: 'self' },
];
export const chargeOptions = (t: TFn): { value: string; label: string; hint: string }[] => [
  { value: 'own',  label: t('p_cdoc_charge_opt_own'),  hint: t('p_cdoc_charge_hint_own') },
  { value: 'cust', label: t('p_cdoc_charge_opt_cust'), hint: t('p_cdoc_charge_hint_cust') },
  { value: 'self', label: t('p_cdoc_charge_opt_self'), hint: t('p_cdoc_charge_hint_self') },
];
/** Which choice a line is on. '' = nobody has said — every line issued before this control existed,
 *  and anything the API sent that these three pairs do not cover. Shown as "not stated" and left
 *  alone unless the operator picks something, so opening an old document cannot answer for them. */
export const chargeChoiceOf = (it: any): string =>
  CHARGE_CHOICES.find(c => c.role === String(it?.chargeRole || '') && c.billed === String(it?.supplierInvoiceBilledTo || ''))?.value || '';
export const chargePatch = (choice: string): any => {
  const c = CHARGE_CHOICES.find(x => x.value === choice);
  return { chargeRole: c ? c.role : '', supplierInvoiceBilledTo: c ? c.billed : '' };
};
/** The contradiction (server code W503): passed through, yet the supplier billed US. Warned, never
 *  blocked — whether the supplier's invoice names the customer is a fact about paperwork on
 *  somebody's desk, and billing does not stop while that is checked. */
export const chargeContradiction = (it: any): boolean =>
  String(it?.chargeRole || '') === 'disbursement' && String(it?.supplierInvoiceBilledTo || '') === 'self';
/** The other way the choice earns nothing, and the likelier mistake: the line ALSO states a VAT
 *  treatment of its own, and the ledger treats a marker as a veto-able claim that may only classify
 *  money nothing else has classified (server services/taxLines.ts disbursementVerdict, code W505).
 *  Worth saying HERE rather than discovering later, because 7% is the default a new line starts on:
 *  tick "paid out for the customer", leave the VAT box alone, and nothing whatsoever happens. */
export const chargeVetoed = (it: any): boolean =>
  String(it?.chargeRole || '') === 'disbursement'
  && ['standard', 'standard_incl', 'zero', 'exempt'].includes(String(it?.vatCategory || 'standard'));

// ── The third condition: passed on at what it cost ──────────────────────────────────────────────
// The hint on the "billed in their name" option has always said all three conditions out loud —
// paid out for the customer, the supplier's invoice in their name, AND "passing the same amount on
// with nothing added". Only the first two were ever recorded, so a line with a margin on it claimed
// the whole amount as somebody else's money, margin included.
//
// So the operator states the one figure that settles it: the TOTAL the supplier charged THEM for
// this line, as it stands on the supplier's bill, whatever VAT that bill carries. It is their own
// declaration, it is on the line where anyone can see it, and — unlike a tick box — the system can
// check it. What it is checked against is what the customer is charged for the pass-through with the
// VAT this line adds left out — see lineChargeBase below for why that, and not base + VAT.
// Optional, never blocking: a blank box claims nothing and behaves exactly as every line behaves
// today (server routes/portalDocs.ts, stored as pass_through_cost).
/** True when the line claims to be money passed through, which is when the question arises. */
export const chargeClaimsPassThrough = (it: any): boolean => String(it?.chargeRole || '') === 'disbursement';
/** What the customer is charged for THIS line, as the W506 comparison means it: the line's TAX
 *  BASE, with the VAT this line adds left out. ⛔ NOT base + VAT (corrected 2026-10-14): that VAT is
 *  collected from the customer and paid over, never kept, so it is margin on nothing — and the first
 *  version of this comparison reported every VAT-bearing line recharged at cost as a 7% mark-up.
 *  The same figure the server's calcItems() stores as pre_vat_amount; see classWarnFor there. */
export const lineChargeBase = (it: any, vatReg: boolean): number => {
  const gross = r2((Number(it?.quantity) || 0) * (Number(it?.unitPrice) || 0));
  // Only an INCLUSIVE line's typed figure has VAT of ours inside it; everywhere else what was typed
  // is already the base.
  return vatReg && String(it?.vatCategory || 'none') === 'standard_incl' ? splitIncl(gross).base : gross;
};
/** The mark-up the operator's own figures describe, or null when there is nothing to say: no cost
 *  stated (0 = nobody has said), or the line passes on no more than it cost. Mirrors the server's
 *  W506 exactly — same figures compared, same strict comparison, same satang rounding, no tolerance.
 *  @param reduces this document takes value OFF an earlier one (a credit note). Nothing is said
 *         then: its line is value coming back, not an amount being passed on, so there is no
 *         "passed on at what it cost" to test and "more" would be the wrong word for both figures.
 *         A debit note adds value and is read exactly like an invoice. Same rule server-side. */
export const chargeMarkup = (it: any, vatReg: boolean, reduces = false): { cost: number; charged: number; diff: number } | null => {
  if (!chargeClaimsPassThrough(it) || reduces) return null;
  const cost = Number(it?.passThroughCost) || 0;
  if (!(cost > 0)) return null;
  const charged = lineChargeBase(it, vatReg);
  return r2(charged) > r2(cost) ? { cost: r2(cost), charged: r2(charged), diff: r2(charged - cost) } : null;
};
/** Every warning this line has earned, in the order it earned them. W503 and W505 keep the
 *  either/or they have always had (the contradiction is the more decisive finding); the mark-up is
 *  a separate fact and can stand beside either of them — the server comma-joins them the same way.
 *  ⛔ A line that SAVING WILL SPLIT is not told "both figures stay on the line, flagged": they will
 *  not — the screen shows the two lines it becomes instead (MarkupSplitPreview). Every line that is
 *  NOT split — an edit, a document not in baht, a non-registrant's (owner, second round) — keeps
 *  the W506 sentence exactly as before.
 *  @param splits saving will split THIS line (the caller's markupSplit(…) came back non-null). */
export const chargeWarnings = (it: any, vatReg: boolean, t: TFn, fmt: (n: number) => string, reduces = false, splits = false): string[] => {
  const w: string[] = [];
  if (chargeContradiction(it)) w.push(t('p_cdoc_charge_w503'));
  else if (chargeVetoed(it)) w.push(t('p_cdoc_charge_w505'));
  const m = splits ? null : chargeMarkup(it, vatReg, reduces);
  if (m) w.push(t('p_cdoc_charge_w506', { cost: fmt(m.cost), charged: fmt(m.charged), diff: fmt(m.diff) }));
  return w;
};

// ── The mark-up is saved as a line of its own, at 7% (owner 2026-10-20) ──────────────────────────
// ⛔ A MIRROR of markupSplitApplies() and splitMarkups() in server/src/routes/portalDocs.ts,
// function for function — the same three document conditions (a document being CREATED, in baht,
// by a VAT-registered merchant: the owner's second round), the same line condition (W506's, on a
// line with no VAT category of its own), the same two lines, the same name, the same VAT mode, the
// same rounding. The server does the splitting; this exists so the operator SEES the two lines, the
// VAT on the added one and the new total before anything is saved, and can retype the added line's
// name. server/src/scripts/testMarkupSplit.ts feeds both the same inputs and requires the same
// lines back — change one side and that suite fails.
/** Whether saving this document may split a marked-up line at all — the server's
 *  markupSplitApplies(m, currency, creating), from what the window knows: a NEW document (an edit
 *  never splits), VAT-registered, in the Thai currency (profile.thaiCurrency, the server's own
 *  thaiCurrency(); unset → nothing splits, as on the server). */
export const markupSplitApplies = (o: { creating: boolean; vatReg: boolean; currency: any; thaiCurrency: any }): boolean => {
  const thb = String(o.thaiCurrency || '').trim().toUpperCase();
  return !!o.creating && !!o.vatReg && !!thb && String(o.currency || '').trim().toUpperCase() === thb;
};
// What the server stores when the operator types nothing; see MARKUP_NAME_SUFFIX there for why
// these words ('Service fee' ↔ 'ค่าบริการ', services/whtIncomeTh.ts). ❓ Awaiting the owner's eye.
export const MARKUP_NAME_SUFFIX = ' — Service fee (ค่าบริการ)';
const LINE_NAME_MAX = 300;
export const markupDefaultName = (name: any): string =>
  `${String(name ?? '').trim().slice(0, LINE_NAME_MAX - MARKUP_NAME_SUFFIX.length)}${MARKUP_NAME_SUFFIX}`;
const markupNameOf = (it: any): string => {
  const typed = typeof it?.markupName === 'string' ? it.markupName.trim() : '';
  return (typed || markupDefaultName(it?.name)).slice(0, LINE_NAME_MAX);
};
const VAT_CATS = ['standard', 'standard_incl', 'zero', 'exempt', 'none'];
/** The server's vatCategoryOf over calcItems' own expression: what the line is priced under. */
export const lineVatCategory = (it: any, vatReg: boolean): string => {
  const raw = it?.vatCategory !== undefined ? it.vatCategory : (it?.vatApplicable === false ? 'none' : 'standard');
  const s = String(raw || '');
  if (VAT_CATS.includes(s)) return vatReg ? s : 'none';
  return vatReg ? 'standard' : 'none';
};
const isNamed = (it: any): boolean => !!String(it?.name ?? '').trim();
/** The document's own VAT mode for the added line: inclusive when every 7% line on it (and there is
 *  one) was entered inclusive, exclusive otherwise — and no VAT at all for a non-registrant. */
export const markupVatCategory = (items: any[], vatReg: boolean): string => {
  const rated = (items || []).filter(isNamed).map(it => lineVatCategory(it, vatReg)).filter(c => c === 'standard' || c === 'standard_incl');
  if (!vatReg) return 'none';
  return rated.length > 0 && rated.every(c => c === 'standard_incl') ? 'standard_incl' : 'standard';
};
// The server's num(), qty and price clamps and passThroughCostOf — exactly, because they decide
// whether a line splits at all, and the warning's own mirror above (lineChargeBase) reads a blank
// quantity as 0 where the server bills it as 1.
const numS = (v: any): number => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const costOf = (v: any): number => {
  if (typeof v !== 'number' && typeof v !== 'string') return 0;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? r2(n) : 0;
};
/** The two lines SAVING turns this one into, or null when it stays one line. Asked only of a
 *  document markupSplitApplies says may split; a non-registrant's line never splits whoever asks
 *  (the server's splitMarkups holds the same rule where the lines are made). */
export const markupSplit = (it: any, items: any[], vatReg: boolean, reduces = false): any[] | null => {
  if (reduces || !vatReg || !isNamed(it)) return null;
  if (lineVatCategory(it, vatReg) !== 'none') return null;   // the line states a VAT treatment of its own (W505's shape)
  const role = typeof it?.chargeRole === 'string' && it.chargeRole === 'disbursement' ? 'disbursement' : '';
  const cost = role ? costOf(it?.passThroughCost) : 0;
  const pv = r2(Math.max(0, numS(it?.quantity) || 1) * Math.max(0, numS(it?.unitPrice)));
  if (!(role === 'disbursement' && cost > 0 && r2(pv) > r2(cost))) return null;
  return [
    // The cost line withholds nothing; the added line keeps the operator's rate and carries NO
    // account and NO charge code — unclassified, for the bookkeeper (owner, second round).
    { ...it, vatCategory: 'none', quantity: 1, unitPrice: cost, passThroughCost: cost, whtRate: 0 },
    { name: markupNameOf(it), description: '', quantity: 1, unitPrice: r2(pv - cost), vatCategory: markupVatCategory(items, vatReg),
      whtRate: it.whtRate, chargeRole: 'own_supply', supplierInvoiceBilledTo: '', passThroughCost: 0,
      code: '', serviceId: '', accountCode: '' },
  ];
};
/** The document's lines as the server will STORE them when it splits — what the totals on screen
 *  must be summed over, so the subtotal, VAT and total shown are the ones the saved document will
 *  carry. Called only where markupSplitApplies is true; otherwise the lines are stored as typed. */
export const expandMarkups = (items: any[], vatReg: boolean, reduces = false): any[] =>
  (items || []).flatMap(it => markupSplit(it, items, vatReg, reduces) || [it]);

// Split a VAT-inclusive gross: take the VAT out FIRST, then subtract. Never gross/1.07 then ×7%,
// which loses a satang on ~6.5% of amounts. Mirrors splitInclusive() on the server.
export const splitIncl = (gross: number) => { const vat = r2(gross * VAT_RATE / (100 + VAT_RATE)); return { base: r2(gross - vat), vat }; };

/** Document totals from the line editor. `preVat` is always the TAX BASE (s.79), in both modes. */
export function lineTotals(items: any[], vatReg: boolean): { preVat: number; vat: number; wht: number; total: number; net: number } {
  let preVat = 0, vat = 0, wht = 0;
  for (const it of items) {
    const gross = r2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0));
    const cat = it.vatCategory || 'none';
    const rated = vatReg && (cat === 'standard' || cat === 'standard_incl');
    let pv: number, v: number;
    if (rated && cat === 'standard_incl') { const s = splitIncl(gross); pv = s.base; v = s.vat; }
    else { pv = gross; v = rated ? r2(gross * VAT_RATE / 100) : 0; }
    // WHT is withheld by the buyer out of the PRE-VAT amount — it never changes the document total,
    // only the cash that actually arrives. It follows the base in both modes.
    // Rounded PER LINE, then summed — the server's calcItems (w = ir2(pv × rate / 100) per line). Summing
    // the unrounded shares showed a WHT / net-to-pay a satang off the stored one on ~1% of multi-line
    // documents (3 of 500 measured 2026-10-20), more often now that a marked-up pass-through saves as two lines.
    preVat = r2(preVat + pv); vat = r2(vat + v); wht = r2(wht + r2(pv * (Number(it.whtRate) || 0) / 100));
  }
  const total = r2(preVat + vat);
  return { preVat, vat, wht, total, net: r2(total - wht) };
}

// ── A credit note's filled line the operator left UNCHANGED (owner 2026-10-20, third round) ──────
// ⛔ A MIRROR of createNoteRoute in server/src/routes/portalDocs.ts, so the difference the window
// shows is the one the server stores. A line filled from the document being credited names the stored
// line it came from (`srcItemId`); while it still carries the fill's own quantity, amount and VAT
// category it takes that stored line's remaining base and VAT exactly (`left`, from GET
// /notes/source) instead of VAT re-derived from the amount — re-deriving misses a part-paid
// receipt's stored VAT by a satang. Edited, it is priced from what was typed, like any line.
// The server decides "unchanged" for itself against the stored line; nothing here is sent to it.
export function noteLineKept(it: any, fills: any[], vatReg: boolean): { base: number; vat: number } | null {
  const sid = String(it?.srcItemId || '');
  if (!sid) return null;
  const f = (fills || []).find(l => String(l?.srcItemId || '') === sid);
  if (!f || !f.left) return null;
  const same = Number(it.quantity) === Number(f.quantity) && Number(it.unitPrice) === Number(f.unitPrice)
    && lineVatCategory(it, vatReg) === lineVatCategory({ vatCategory: f.vatCategory }, vatReg);
  return same ? { base: Number(f.left.base) || 0, vat: Number(f.left.vat) || 0 } : null;
}
/** A credit note's difference as the server will store it: unchanged filled lines at their stored
 *  remainder (noteLineKept), every other line through lineTotals.
 *  `wht` (owner 2026-10-20, third round — Q4): the withholding the note will STORE and PRINT ("WHT
 *  Amount", and "Payment Amount" = total − it) — per line out of the base that line is stored with
 *  (the stored remainder on an unchanged filled line), rounded per line, at a rate the server
 *  accepts (WHT_RATES; anything else it stores as 0), exactly as createNoteRoute → calcItems does. */
export function noteTotals(items: any[], fills: any[], vatReg: boolean): { preVat: number; vat: number; wht: number } {
  const loose: any[] = [];
  let base = 0, vat = 0, wht = 0;
  const rateOf = (it: any): number => (WHT_RATES.includes(Number(it?.whtRate)) ? Number(it.whtRate) : 0);
  for (const it of items || []) {
    const k = noteLineKept(it, fills, vatReg);
    if (k) { base = r2(base + k.base); vat = r2(vat + k.vat); wht = r2(wht + r2(k.base * rateOf(it) / 100)); }
    else loose.push({ ...it, whtRate: rateOf(it) });
  }
  const t = lineTotals(loose, vatReg);
  return { preVat: r2(t.preVat + base), vat: r2(t.vat + vat), wht: r2(t.wht + wht) };
}

// ── Mark-up round 5 (2026-10-27): the return box of a credit-note line, and the line's money ─────────────────
// ⛔ A MIRROR of services/merchantBooking.ts lineReturnBox (lineBucket) for a line as the window holds it, so the window
// says before Issue what the server would refuse (credit_cat_left) — the server decides for itself all the same (and the
// window's Issue asks the server's own preflight first). 7% → 'rated'; 0% → 'zero'; exempt → 'exempt'; no VAT → 'outscope'
// when it is money passed through at cost on an invoice addressed to the customer (charge role 'disbursement', billed to
// 'customer' — the one marker the ledger honours), otherwise 'none'. A merchant outside VAT: everything is 'none'.
export const NOTE_NON_VAT_BOXES = ['zero', 'exempt', 'outscope', 'none'];
export function noteBoxOf(it: any, vatReg: boolean): string {
  const c = lineVatCategory(it, vatReg);
  if (c === 'standard' || c === 'standard_incl') return 'rated';
  if (c === 'zero' || c === 'exempt') return c;
  return String(it?.chargeRole || '') === 'disbursement' && String(it?.supplierInvoiceBilledTo || '') === 'customer' ? 'outscope' : 'none';
}
/** A return box in the operator's language. */
export const noteBoxWord = (t: TFn, box: any): string => {
  const k = ({ zero: 'p_note_box_zero', exempt: 'p_note_box_exempt', outscope: 'p_note_box_outscope', none: 'p_note_box_none' } as Record<string, string>)[String(box || '')];
  return k ? String(t(k)) : String(box || '');
};
/** A credit-note line's money as the server will store it (base + VAT): an unchanged filled line at its stored remainder,
 *  anything else priced as typed — noteTotals' own two halves, one line at a time. */
export function noteLineGross(it: any, fills: any[], vatReg: boolean): number {
  const k = noteLineKept(it, fills, vatReg);
  if (k) return r2(k.base + k.vat);
  const x = lineTotals([it], vatReg);
  return r2(x.preVat + x.vat);
}

// ── A refusal the server CODED, in the operator's language (third + fourth rounds, 2026-10-20) ──────
// Each of these server refusals keeps its English sentence in `error` — what every other caller reads
// — and also sends `code` + `params` (the same figures), so the window says it in the operator's own
// language. ONE copy, read by the note window, the document window and the portal's Void action.
// '' for any refusal that carries no code here: the caller then shows the server's own words.
// `fm` formats a figure the way the calling window formats its own money.
export function refusalText(t: TFn, e: any, fm: (n: number) => string): string {
  const c = String(e?.payload?.code || ''), p = e?.payload?.params || {};
  const s = (v: any): string => String(v ?? '');
  const n = (v: any): string => fm(Number(v) || 0);
  const cat = (v: any): string => vatOptions(t).find(o => o.value === s(v))?.label || s(v);
  switch (c) {
    // What credit notes together may take off a document: its total…
    case 'credit_cap_line':
      return t('p_note_cap_line', { line: s(p.line), docNo: s(p.docNo), left: n(p.left), charged: n(p.charged), credited: n(p.credited), takes: n(p.takes) });
    case 'credit_cap_doc':
      // …which a debit note against it raises (fourth round) — said, where one has.
      if (s(p.dnNos)) return t(p.nos ? 'p_note_cap_doc_dn_by' : 'p_note_cap_doc_dn', { docNo: s(p.docNo), left: n(p.left), issued: n(p.issued), debited: n(p.debited), dnNos: s(p.dnNos), credited: n(p.credited), nos: s(p.nos), takes: n(p.takes) });
      return t(p.nos ? 'p_note_cap_doc_by' : 'p_note_cap_doc', { docNo: s(p.docNo), left: n(p.left), total: n(p.total), credited: n(p.credited), nos: s(p.nos), takes: n(p.takes) });
    // …and, since the fourth round, its output VAT; and a filled line keeps its VAT category.
    case 'credit_vat_cap_line':
      return t('p_note_vatcap_line', { line: s(p.line), docNo: s(p.docNo), left: n(p.left), charged: n(p.charged), credited: n(p.credited), takes: n(p.takes) });
    case 'credit_vat_cap_doc':
      // The VAT a debit note added is said as such, as the total's refusal says its value.
      if (s(p.dnNos)) return t('p_note_vatcap_doc_dn', { docNo: s(p.docNo), left: n(p.left), issued: n(p.issued), debited: n(p.debited), dnNos: s(p.dnNos), credited: n(p.credited), takes: n(p.takes) });
      return t('p_note_vatcap_doc', { docNo: s(p.docNo), left: n(p.left), total: n(p.total), credited: n(p.credited), takes: n(p.takes) });
    case 'credit_cat_locked':
      return t('p_note_cat_locked', { n: s(p.n), line: s(p.line), docNo: s(p.docNo), category: cat(p.category) });
    // (Mark-up round 5, item 2) Lines typed by hand take more of a NON-VAT return box than the document (and its debit
    // notes) still hold in it — named by the box and what is left.
    case 'credit_cat_left':
      // (Review fix) The two no-VAT boxes differ only by the charge choice: when the other one still holds value, the
      // sentence says how much and which choice takes it back (p.sibling: 'outscope' → paid out for the customer, billed
      // in their name; 'none' → our own service or goods).
      if ((s(p.sibling) === 'outscope' || s(p.sibling) === 'none') && Number(p.siblingLeft) > 0.005) {
        return t(s(p.sibling) === 'outscope' ? 'p_note_err_cat_left_out' : 'p_note_err_cat_left_own', {
          docs: [s(p.docNo), ...(s(p.dnNos) ? [s(p.dnNos)] : [])].join(', '), left: n(p.left), takes: n(p.takes), sibLeft: n(p.siblingLeft),
          charge: t(s(p.sibling) === 'outscope' ? 'p_cdoc_charge_opt_cust' : 'p_cdoc_charge_opt_own') });
      }
      return t(s(p.dnNos) ? 'p_note_err_cat_left_dn' : 'p_note_err_cat_left',
        { docNo: s(p.docNo), dnNos: s(p.dnNos), category: noteBoxWord(t, p.category), left: n(p.left), takes: n(p.takes) });
    // (Review fix) A filled line's pointer after value was taken off without naming lines (the fill offers none then).
    case 'credit_fill_unlinked':
      return t('p_note_err_fill_unlinked', { docNo: s(p.docNo), nos: s(p.nos) });
    // (Review fix) A filled line keeps the return box it was charged in — its charge choice as well as its VAT category.
    case 'credit_box_locked':
      return t('p_note_err_box_locked', { n: s(p.n), line: s(p.line), docNo: s(p.docNo), box: noteBoxWord(t, p.box) });
    // (Mark-up round 5, item 3) No-VAT value a document booked in the 7% column at no VAT, taken back with VAT lines.
    case 'credit_none_flat_vat':
      return t('p_note_err_none_flat_vat', { docNos: s(p.docNos), amount: n(p.amount) });
    // (Mark-up round 5, item 1) A credit note dated before a document it draws on — the one named, or a debit note.
    case 'note_before_source': {
      const w = docTypeWord(t, s(p.docType) === 'receipt' ? 'tax_invoice_receipt' : p.docType);
      return t('p_note_err_before_source', { ...w, docNo: s(p.docNo), date: s(p.date), noteDate: s(p.noteDate) });
    }
    // An edit from a page that does not name its lines, on a document with a mark-up line (409).
    case 'edit_stale_page':
      return t('p_cdoc_edit_stale', { docNo: s(p.docNo) });
    // (Owner 2026-11-02) A quotation marked Rejected: nothing bills it, edits it or sends it (services/quotationStage.ts).
    case 'quotation_rejected': return t('p_conv_rejected', { docNo: s(p.docNo) });
    // (Owner 2026-11-02) What a quotation may become (portalDocs.ts quotationConvertVerdict).
    case 'conv_already': return t('p_conv_err_already', { nos: s(p.nos), docNo: s(p.docNo) });
    case 'conv_tax_unpaid': return t('p_conv_err_tax_unpaid', { docNo: s(p.docNo) });
    case 'conv_void': return t('p_conv_err_void');
    case 'conv_world': return t(p.plain ? 'p_conv_err_world_plain' : 'p_conv_err_world_thai');
    case 'conv_customer_gone': return t('p_conv_err_customer_gone', { docNo: s(p.docNo) });
    case 'conv_customer': return t('p_conv_err_customer', { docNo: s(p.docNo), customer: s(p.customer) });
    case 'conv_no_lines': return t('p_conv_err_no_lines');
    case 'conv_type': return t('p_conv_err_type');
    case 'conv_not_found': return t('p_conv_err_not_found');
    case 'conv_receipt_needs_bill': return t('p_conv_err_receipt_needs_bill', { docNo: s(p.docNo) });
    case 'conv_nothing_owed': return t('p_conv_err_nothing_owed', { nos: s(p.nos) });
    case 'conv_cash_date': return t('p_conv_err_cash_date');
    case 'conv_cash_future': return t('p_conv_err_cash_future');
    case 'conv_filed': return t('p_conv_err_filed', { ym: s(p.ym), date: s(p.date) });
    case 'conv_fx_approval': return t('p_conv_err_fx_approval', { currency: s(p.currency) });
    // (Review 2026-11-02) The lines would not bill as quoted: a pass-through marked up over its cost, or a VAT registration that has changed. The figures are the server's.
    case 'conv_repriced': return p.split ? t('p_conv_err_repriced_split', { docNo: s(p.docNo) }) : t('p_conv_err_repriced_vat', { docNo: s(p.docNo), quoted: n(p.quoted), total: n(p.total) });
    // (Review 2026-11-02) A credit note that freed its quotation: voided, it would bring its document back beside what billed it next, or onto a Rejected quotation.
    case 'credit_void_rebilled': return t('p_doc_void_credit_rebilled', { noteNo: s(p.noteNo), docNo: s(p.docNo), quoteNo: s(p.quoteNo), nos: s(p.nos) });
    case 'credit_void_rejected': return t('p_doc_void_credit_rejected', { noteNo: s(p.noteNo), docNo: s(p.docNo), quoteNo: s(p.quoteNo) });
    // (Recheck 2026-11-02) …and a debit note on such a document, once something else bills its quotation: it goes on the standing bill.
    case 'note_debit_rebilled': return t(p.ordinary ? 'p_note_err_debit_rebilled_invoice' : 'p_note_err_debit_rebilled', { docNo: s(p.docNo), quoteNo: s(p.quoteNo), nos: s(p.nos) });
    // (Owner 2026-11-02) A copied form's customer removed while it stood open (portalDocs.ts docCopyGate).
    case 'copy_customer_gone': return t('p_copy_err_customer_gone');
    // A foreign-currency tax invoice with no rate, refused before a number is taken.
    case 'fx_rate_missing':
      return t('p_cdoc_fx_rate_missing', { currency: s(p.currency) });
    // (COA-D6) A tax invoice issued FOR an ordinary invoice already paid (services/taxInvoiceFor.ts): why it may not be.
    case 'tax_for_type': return t('p_taxfor_err_type');
    case 'tax_for_not_vat': return t('p_taxfor_err_not_vat');
    case 'tax_for_not_found': return t('p_taxfor_err_not_found');
    case 'tax_for_void': return t('p_taxfor_err_void', { docNo: s(p.docNo) });
    case 'tax_for_not_invoice': return t('p_taxfor_err_not_invoice', { docNo: s(p.docNo) });
    case 'tax_for_plain': return t('p_taxfor_err_plain', { docNo: s(p.docNo) });
    case 'tax_for_customer': return t('p_taxfor_err_customer', { docNo: s(p.docNo), customer: s(p.customer) });
    case 'tax_for_unpaid':
      return Number(p.received) > 0.005
        ? t('p_taxfor_err_unpaid_part', { docNo: s(p.docNo), received: n(p.received), total: n(p.total), receiptNos: s(p.receiptNos) })
        : t('p_taxfor_err_unpaid_none', { docNo: s(p.docNo) });
    case 'tax_for_adjusted': return t('p_taxfor_err_adjusted', { docNo: s(p.docNo), nos: s(p.nos) });
    case 'tax_for_taken': return t('p_taxfor_err_taken', { docNo: s(p.docNo), tivNo: s(p.tivNo) });
    case 'tax_for_currency': return t('p_taxfor_err_currency', { docNo: s(p.docNo), currency: s(p.currency) });
    case 'tax_for_amount':
      return t('p_taxfor_err_amount', { docNo: s(p.docNo), receiptNos: s(p.receiptNos), paid: n(p.paid), paidVat: n(p.paidVat), total: n(p.total), vat: n(p.vat) });
    case 'tax_for_not_chain': return t('p_taxfor_err_not_chain', { quoteNo: s(p.quoteNo) });
    // (b14) …its date: before the sale it is for, inside a closed period, in a filed month.
    case 'tax_for_before':
      return t('p_taxfor_err_before', { date: s(p.date), earliest: s(p.earliest), docNo: s(p.docNo), invoiceDate: s(p.invoiceDate), paidDate: s(p.paidDate), receiptNos: s(p.receiptNos) });
    case 'tax_for_closed': return t('p_taxfor_err_closed', { date: s(p.date), from: s(p.periodStart), to: s(p.periodEnd), docNo: s(p.docNo) });
    case 'tax_for_filed': return t('p_taxfor_err_filed', { ym: s(p.ym), date: s(p.date), docNo: s(p.docNo) });
    case 'tax_for_choose': return t('p_taxfor_err_choose', { quoteNo: s(p.quoteNo), docNos: s(p.docNos) });
    // …a note on such a tax invoice (it booked nothing — its receipt did), and a void of the receipt it stands on.
    case 'note_on_tax_for': return t('p_note_err_tax_for', { docNo: s(p.docNo), forNo: s(p.forNo), receiptNos: s(p.receiptNos) });
    case 'void_tax_for_first': return t('p_doc_void_tax_for_first', { docNo: s(p.docNo), invoiceNo: s(p.invoiceNo), tivNo: s(p.tivNo) });
    // (b14) …and a credit or debit note on the sale such a tax invoice stands on — its receipt, or the paid invoice itself.
    case 'note_tax_for_first':
      return t(s(p.kind) === 'receipt' ? 'p_note_err_tax_for_first_receipt' : 'p_note_err_tax_for_first_invoice',
        { docNo: s(p.docNo), invoiceNo: s(p.invoiceNo), receiptNos: s(p.receiptNos), tivNo: s(p.tivNo) });
    // A debit note credit notes already draw on cannot be voided from under them.
    case 'debit_void_credited':
      return t('p_doc_void_dn_credited', { docNo: s(p.docNo), nos: s(p.nos), dnNo: s(p.dnNo) });
    // (Mark-up round 5, review fix) …nor one they take value from in another way: typed non-VAT value placed on it (named
    // by its box), or its share of their excess when voiding it would pass that share to a debit note dated after one.
    case 'debit_void_drawn':
      if (s(p.nextNo)) return t('p_doc_void_dn_drawn_later', { docNo: s(p.docNo), nos: s(p.nos), dnNo: s(p.dnNo), nextNo: s(p.nextNo), date: s(p.date), cnNo: s(p.cnNo), cnDate: s(p.cnDate) });
      return t('p_doc_void_dn_drawn', { docNo: s(p.docNo), nos: s(p.nos), dnNo: s(p.dnNo), box: noteBoxWord(t, p.box) });
    // (Round 4.1, decision 1) A credit note on a receipt stands only against a bill paid in full: refused while
    // the bill owes anything, and a receipt void that would reopen such a bill is refused too.
    case 'credit_receipt_bill_owes':
      return t('p_note_err_receipt_bill_owes', { ...docTypeWord(t, p.docType), invoiceNo: s(p.invoiceNo), receiptNo: s(p.receiptNo), outstanding: n(p.outstanding) });
    case 'receipt_void_credited':
      return t('p_doc_void_receipt_credited', { ...docTypeWord(t, p.docType), docNo: s(p.docNo), invoiceNo: s(p.invoiceNo), outstanding: n(p.outstanding), nos: s(p.nos), receiptNos: s(p.receiptNos) });
    // (Round 4.1 review) …and the receipt's own DEBIT notes: a credit note on a receipt waits until they are paid in
    // full too, and a receipt of such a debit note is not voided while a credit note stands on the receipt it adjusts.
    case 'credit_receipt_debit_owes':
      return t('p_note_err_receipt_debit_owes', { dnNo: s(p.dnNo), receiptNo: s(p.receiptNo), outstanding: n(p.outstanding) });
    case 'receipt_void_debit_credited':
      return t('p_doc_void_receipt_debit_credited', { docNo: s(p.docNo), invoiceNo: s(p.invoiceNo), outstanding: n(p.outstanding), nos: s(p.nos), receiptNos: s(p.receiptNos) });
    // (Round 4.1 re-verification) A void that must wait for a note which cannot be voided now: that note, and its own
    // refusal said the way the window says it when that note's Void is pressed (`inner`, the body it answers).
    case 'void_note_held': {
      const w = docTypeWord(t, p.noteType);
      return t('p_doc_void_note_held', { docNo: s(p.docNo), noteNo: s(p.noteNo), noteWord: w.docWord, noteWordLc: w.docWordLc, reason: innerVoidText(t, p.inner, fm) });
    }
  }
  return '';
}
/** The refusal a note's own Void would answer (void_note_held's `inner`), in the operator's language where it is coded —
 *  the in-books lock as the list words it on a greyed Void — else in the server's words. */
const innerVoidText = (t: TFn, inner: any, fm: (n: number) => string): string => {
  const e = { payload: inner && typeof inner === 'object' ? inner : {} };
  if (e.payload.code === 'in_books') return t('p_ll_void_locked', { docNos: (Array.isArray(e.payload.params?.docNos) ? e.payload.params.docNos : []).join(', ') });
  return refusalText(t, e, fm) || docRefusalText(e, t) || String(e.payload.error || '');
};

/** UK day-first (owner 2026-11-02, the staff screens): every date a refusal carries, however deep (a note's own refusal in `inner`), before the wording runs. ⛔ For a staff window, and not inside
 *  the two functions above: the portal's sentences stay as they are. */
export const dayFirstDeep = (v: any): any => {
  if (typeof v === 'string') return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split('-').reverse().join('/') : v;
  if (Array.isArray(v)) return v.map(dayFirstDeep);
  return v && typeof v === 'object' ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, dayFirstDeep(x)])) : v;
};

export const today = (): string => todayISO();
// Payment terms. An invoice without a deadline is not a demand for payment, so the due date is
// required and defaults to issue + 30 days. Computed in UTC off the date string itself so it can
// never drift a day with the browser's timezone.
export const PAYMENT_TERM_DAYS = 30;
export const addDays = (iso: string, days: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '')); if (!m) return '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
// Withholding rates a Thai payer may certify on ภ.ง.ด.3 / 53. 0 = the buyer withholds nothing.
export const WHT_RATES = [0, 1, 2, 3, 5, 10, 15];

// ── Pictures in a document's Remark ───────────────────────────────────────────────────────
// A stored Remark carries `<img src="/api/portal/doc-images/…">`, and that is an authenticated GET:
// an <img> the browser loads by itself sends no Authorization header, so it would 401 and show as
// a broken icon. While the dialog is open every picture is therefore shown from an OBJECT URL —
// fetched with the token when the document is opened, made straight from the file when one is
// pasted — and the canonical URL is put back at save time.
//
// Putting it back matters: the server's sanitiser admits our own upload URLs and nothing else, so
// a blob: that reached it would not be stored as a broken image, it would be dropped entirely.
// (The same approach as Bugify's useInlineImages; see components/Bugify/index.tsx.)
const DOC_IMG_URL = /\/api\/portal\/doc-images\/[A-Za-z0-9-]{8,64}/g;
export function useRemarkImages(docApi: MerchantDocApi) {
  const map = useRef<Map<string, string>>(new Map());   // object URL → /api/portal/doc-images/<id>
  useEffect(() => () => { for (const u of Array.from(map.current.keys())) URL.revokeObjectURL(u); }, []);

  const onImage = useCallback(async (f: File) => {
    const r: any = await docApi.uploadDocImage(f);
    const local = URL.createObjectURL(f);              // shown instantly — no round trip to display it
    map.current.set(local, r.url);
    return local;
  }, []);

  /** Stored value → what the editor can show. Unchanged when it holds no pictures. */
  const seed = useCallback(async (html: string): Promise<string> => {
    const urls = Array.from(new Set(String(html || '').match(DOC_IMG_URL) || []));
    if (!urls.length) return html;
    let out = html;
    for (const u of urls) {
      // An image that will not load is left as it is rather than removed: dropping it here would
      // silently delete the picture from the document the moment anything else was saved.
      try { const local = await docApi.docImageBlob(u); map.current.set(local, u); out = out.split(u).join(local); }
      catch { /* keep the canonical URL */ }
    }
    return out;
  }, []);

  /** What leaves the browser: every object URL back to the URL the server will serve. */
  const finalise = useCallback((html: string) => {
    let out = html;
    for (const [local, real] of Array.from(map.current.entries())) out = out.split(local).join(real);
    return out;
  }, []);

  return { onImage, seed, finalise };
}
